import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Save, ImagePlus, X, Plus, Trash2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  useGetDealQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
} from "@/store";
import { toast } from "sonner";
import type {
  CreateDealData,
  ComponentType,
  DealType
} from "@/types/deals";
import type { Media } from "@/types";
import { MediaSelector } from "@/components/media";

interface DealFormData extends Omit<CreateDealData, 'components'> {
  components: DealComponentFormData[];
}

interface DealComponentFormData {
  id?: string;
  name: string;
  description?: string;
  componentType: ComponentType;
  required: boolean;
  order: number;
  options: DealComponentOptionFormData[];
}

interface DealComponentOptionFormData {
  id?: string;
  name: string;
  description?: string;
  price: number;
  quantity: number;
  unit: string;
  inStock: boolean;
  order: number;
}

export function DealFormScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);
  const [components, setComponents] = useState<DealComponentFormData[]>([]);
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [editingComponentIndex, setEditingComponentIndex] = useState<number | null>(null);
  const [currentComponent, setCurrentComponent] = useState<DealComponentFormData>({
    name: "",
    description: "",
    componentType: "CHOICE",
    required: false,
    order: 0,
    options: [],
  });
  const formPopulatedRef = useRef<Set<string>>(new Set());

  const { data: dealData, isLoading: isLoadingDeal } = useGetDealQuery(id || "", {
    skip: !id,
  });
  const [createDeal, { isLoading: isCreating }] = useCreateDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<DealFormData>({
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      originalPrice: undefined,
      image: "",
      images: [],
      isActive: true,
      featured: false,
      dealType: "FIXED",
      components: [],
    },
  });

  const dealType = watch("dealType");

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && dealData?.data && id) {
      const deal = dealData.data;

      // Only populate if we haven't populated for this deal ID
      if (formPopulatedRef.current.has(id)) {
        return;
      }

      // Set all form values
      setValue("name", deal.name);
      setValue("description", deal.description);
      setValue("price", deal.price);
      setValue("originalPrice", deal.originalPrice || undefined);
      setValue("image", deal.image);
      setValue("images", deal.images || []);
      setValue("isActive", deal.isActive);
      setValue("featured", deal.featured);
      setValue("dealType", deal.dealType);

      // Set components
      const formComponents: DealComponentFormData[] = deal.components.map((component) => ({
        id: component.id,
        name: component.name,
        description: component.description,
        componentType: component.componentType,
        required: component.required,
        order: component.order,
        options: component.options.map((option) => ({
          id: option.id,
          name: option.name,
          description: option.description,
          price: option.price,
          quantity: option.quantity,
          unit: option.unit,
          inStock: option.inStock,
          order: option.order,
        })),
      }));
      setComponents(formComponents);

      formPopulatedRef.current.add(id);
    }
  }, [dealData, isEditMode, setValue, id]);

  const addComponent = () => {
    setCurrentComponent({
      name: "",
      description: "",
      componentType: "CHOICE",
      required: false,
      order: components.length,
      options: [],
    });
    setEditingComponentIndex(null);
    setComponentDialogOpen(true);
  };

  const editComponent = (index: number) => {
    setCurrentComponent({ ...components[index] });
    setEditingComponentIndex(index);
    setComponentDialogOpen(true);
  };

  const deleteComponent = (index: number) => {
    const newComponents = components.filter((_, i) => i !== index);
    setComponents(newComponents);
  };

  const saveComponent = () => {
    if (!currentComponent.name.trim()) {
      toast.error("Component name is required");
      return;
    }

    if (currentComponent.options.length === 0) {
      toast.error("At least one option is required");
      return;
    }

    const newComponents = [...components];
    if (editingComponentIndex !== null) {
      newComponents[editingComponentIndex] = { ...currentComponent };
    } else {
      newComponents.push({ ...currentComponent });
    }

    setComponents(newComponents);
    setComponentDialogOpen(false);
    setCurrentComponent({
      name: "",
      description: "",
      componentType: "CHOICE",
      required: false,
      order: 0,
      options: [],
    });
  };

  const addComponentOption = () => {
    const newOption: DealComponentOptionFormData = {
      name: "",
      description: "",
      price: 0,
      quantity: 1,
      unit: "litre",
      inStock: true,
      order: currentComponent.options.length,
    };
    setCurrentComponent({
      ...currentComponent,
      options: [...currentComponent.options, newOption],
    });
  };

  const updateComponentOption = (index: number, field: keyof DealComponentOptionFormData, value: any) => {
    const newOptions = [...currentComponent.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setCurrentComponent({
      ...currentComponent,
      options: newOptions,
    });
  };

  const removeComponentOption = (index: number) => {
    const newOptions = currentComponent.options.filter((_, i) => i !== index);
    setCurrentComponent({
      ...currentComponent,
      options: newOptions,
    });
  };

  const onSubmit = async (data: DealFormData) => {
    try {
      // Validate components
      if (components.length === 0) {
        toast.error("At least one component is required");
        return;
      }

      const dealData = {
        ...data,
        components: components.map((component, index) => ({
          name: component.name,
          description: component.description,
          componentType: component.componentType,
          required: component.required,
          order: index,
          options: component.options.map((option, optionIndex) => ({
            name: option.name,
            description: option.description,
            price: option.price,
            quantity: option.quantity,
            unit: option.unit,
            inStock: option.inStock,
            order: optionIndex,
          })),
        })),
      };

      if (isEditMode && id) {
        await updateDeal({ id, data: dealData }).unwrap();
        toast.success("Deal updated successfully");
      } else {
        await createDeal(dealData).unwrap();
        toast.success("Deal created successfully");
      }

      navigate("/admin/deals");
    } catch (error) {
      console.error("Failed to save deal:", error);
      toast.error(isEditMode ? "Failed to update deal" : "Failed to create deal");
    }
  };

  if (isLoadingDeal) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/deals")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditMode ? "Edit Deal" : "Create Deal"}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode
              ? "Update deal information and components"
              : "Create a new deal with customizable components"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Enter the basic details for your deal
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Deal Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Deal name is required" })}
                  placeholder="e.g., Royale Pot"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="dealType">Deal Type *</Label>
                <Select
                  value={dealType}
                  onValueChange={(value: DealType) => setValue("dealType", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select deal type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIXED">Fixed - All items predetermined</SelectItem>
                    <SelectItem value="CONFIGURABLE">Configurable - User selects from options</SelectItem>
                    <SelectItem value="COMPLEX">Complex - Multiple choice categories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                {...register("description", { required: "Description is required" })}
                placeholder="Describe your deal..."
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (₦) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  {...register("price", {
                    required: "Price is required",
                    min: { value: 0, message: "Price must be positive" }
                  })}
                  placeholder="0.00"
                />
                {errors.price && (
                  <p className="text-sm text-destructive">{errors.price.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="originalPrice">Original Price (₦)</Label>
                <Input
                  id="originalPrice"
                  type="number"
                  step="0.01"
                  {...register("originalPrice")}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Deal Image *</Label>
              <input
                type="hidden"
                {...register("image", {
                  required: "Deal image is required",
                })}
              />
              <div className="flex items-center gap-4">
                {watch("image") ? (
                  <div className="relative">
                    <img
                      src={watch("image")}
                      alt="Deal"
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0"
                      onClick={() => setValue("image", "")}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMediaSelectorOpen(true)}
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Select Image
                  </Button>
                )}
              </div>
              {errors.image && (
                <p className="text-sm text-destructive">{errors.image.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={watch("isActive")}
                  onCheckedChange={(checked) => setValue("isActive", !!checked)}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="featured"
                  checked={watch("featured")}
                  onCheckedChange={(checked) => setValue("featured", !!checked)}
                />
                <Label htmlFor="featured">Featured</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Components */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Deal Components</CardTitle>
                <CardDescription>
                  Define the components that make up this deal
                </CardDescription>
              </div>
              <Button type="button" onClick={addComponent}>
                <Plus className="w-4 h-4 mr-2" />
                Add Component
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {components.length === 0 ? (
              <div className="text-center py-8">
                <Settings className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No components added</h3>
                <p className="text-muted-foreground mt-2">
                  Add components to define what your deal includes
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {components.map((component, index) => (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{component.name}</CardTitle>
                          <CardDescription>{component.description}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {component.componentType.replace("_", " ")}
                          </Badge>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => editComponent(index)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteComponent(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Options:</p>
                        <div className="flex flex-wrap gap-2">
                          {component.options.map((option, optionIndex) => (
                            <Badge key={optionIndex} variant="secondary">
                              {option.name} ({option.quantity} {option.unit})
                              {option.price > 0 && ` +₦${option.price}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/deals")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating || isUpdating}>
            <Save className="w-4 h-4 mr-2" />
            {isCreating || isUpdating
              ? (isEditMode ? "Updating..." : "Creating...")
              : (isEditMode ? "Update Deal" : "Create Deal")}
          </Button>
        </div>
      </form>

      {/* Component Dialog */}
      <Dialog open={componentDialogOpen} onOpenChange={setComponentDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingComponentIndex !== null ? "Edit Component" : "Add Component"}
            </DialogTitle>
            <DialogDescription>
              Define a component for your deal
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="componentName">Component Name *</Label>
                <Input
                  id="componentName"
                  value={currentComponent.name}
                  onChange={(e) => setCurrentComponent({
                    ...currentComponent,
                    name: e.target.value
                  })}
                  placeholder="e.g., Soup, Protein, Addons"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="componentType">Component Type *</Label>
                <Select
                  value={currentComponent.componentType}
                  onValueChange={(value: ComponentType) => setCurrentComponent({
                    ...currentComponent,
                    componentType: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIXED">Fixed - Pre-selected</SelectItem>
                    <SelectItem value="CHOICE">Choice - User picks one</SelectItem>
                    <SelectItem value="MULTI_CHOICE">Multi Choice - User picks multiple</SelectItem>
                    <SelectItem value="OPTIONAL">Optional - User can add/remove</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="componentDescription">Description</Label>
              <Textarea
                id="componentDescription"
                value={currentComponent.description || ""}
                onChange={(e) => setCurrentComponent({
                  ...currentComponent,
                  description: e.target.value
                })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="componentRequired"
                checked={currentComponent.required}
                onCheckedChange={(checked) => setCurrentComponent({
                  ...currentComponent,
                  required: !!checked
                })}
              />
              <Label htmlFor="componentRequired">Required component</Label>
            </div>

            {/* Component Options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Options</Label>
                <Button type="button" size="sm" onClick={addComponentOption}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Option
                </Button>
              </div>

              {currentComponent.options.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No options added. Add at least one option.
                </p>
              ) : (
                <div className="space-y-3">
                  {currentComponent.options.map((option, index) => (
                    <Card key={index} className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm">Name *</Label>
                          <Input
                            value={option.name}
                            onChange={(e) => updateComponentOption(index, "name", e.target.value)}
                            placeholder="e.g., Egusi"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Quantity *</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={option.quantity}
                            onChange={(e) => updateComponentOption(index, "quantity", parseFloat(e.target.value) || 1)}
                            placeholder="1"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Unit</Label>
                          <Select
                            value={option.unit}
                            onValueChange={(value) => updateComponentOption(index, "unit", value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="litre">Litre</SelectItem>
                              <SelectItem value="kg">Kg</SelectItem>
                              <SelectItem value="pieces">Pieces</SelectItem>
                              <SelectItem value="servings">Servings</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Extra Price (₦)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={option.price}
                            onChange={(e) => updateComponentOption(index, "price", parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`option-instock-${index}`}
                            checked={option.inStock}
                            onCheckedChange={(checked) => updateComponentOption(index, "inStock", !!checked)}
                          />
                          <Label htmlFor={`option-instock-${index}`} className="text-sm">
                            In Stock
                          </Label>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeComponentOption(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setComponentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={saveComponent}>
              {editingComponentIndex !== null ? "Update Component" : "Add Component"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Selector */}
      <MediaSelector
        open={mediaSelectorOpen}
        onOpenChange={setMediaSelectorOpen}
        onSelect={(media) => {
          const selectedMedia = media as Media;
          setValue("image", selectedMedia.url, { shouldValidate: true });
        }}
        multiple={false}
        folder="products"
      />
    </div>
  );
}